import CategoryCarousel from "../../components/CategoryHome/Category";
import React, { useState, useEffect, Suspense, useRef } from "react";
import axios from "axios";
import BASE_URL from "../../configs/api";
import Loading from "../../components/Loading/Loading";
import ContinueWatching from "../../components/ContinueWatching/ContinueWatching";
import { useLocation } from "react-router-dom";
// import TopPhimBo from "../../components/CategoryHome/TopPhimBo"; // Xóa import tĩnh
// import RankMovie from "../../components/CategoryHome/RankMovie"; // Xóa import tĩnh
import HeroSection from "../../components/Hero/Hero";
import { Helmet } from "react-helmet";

// --- React.lazy cho các component tải sau ---
// Thêm TopPhimBo và RankMovie vào danh sách lazy-load
const TopPhimBo = React.lazy(() =>
  import("../../components/CategoryHome/TopPhimBo")
);
const RankMovie = React.lazy(() =>
  import("../../components/CategoryHome/RankMovie")
);
const PosterCarousel = React.lazy(() =>
  import("../../components/CategoryHome/PosterCarousel")
);
const MovieHot = React.lazy(() =>
  import("../../components/CategoryHome/MovieHot")
);
const AnimeList = React.lazy(() =>
  import("../../components/CategoryHome/AnimeList")
);

// Custom hook useScrollAnimation không thay đổi, giữ nguyên
const useScrollAnimation = () => {
  const [visibleElements, setVisibleElements] = useState(new Set());
  const elementsRef = useRef([]);

  useEffect(() => {
    document.title = "MePhim - Xem Phim Online Miễn Phí!";
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("movie-title-")) {
        localStorage.removeItem(key);
      }
    });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements(
              (prev) => new Set([...prev, entry.target.dataset.animationId])
            );
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    elementsRef.current.forEach((element) => {
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const addElementRef = (element, id) => {
    if (element && !elementsRef.current.includes(element)) {
      element.dataset.animationId = id;
      elementsRef.current.push(element);
    }
  };

  return { visibleElements, addElementRef };
};

// Component AnimatedSection không thay đổi, giữ nguyên
const AnimatedSection = ({
  children,
  animationId,
  animationType = "fadeInUp",
  delay = 0,
}) => {
  const { visibleElements, addElementRef } = useScrollAnimation();
  const isVisible = visibleElements.has(animationId);

  const getAnimationClass = () => {
    const baseClass = "transition-all duration-1000 ease-out";
    if (!isVisible) {
      switch (animationType) {
        case "slideInUp":
          return `${baseClass} opacity-0 translate-y-12`;
        default:
          return `${baseClass} opacity-0 translate-y-8`;
      }
    }
    return `${baseClass} opacity-100 translate-y-0 translate-x-0 scale-100`;
  };

  return (
    <div
      ref={(el) => addElementRef(el, animationId)}
      className={getAnimationClass()}
      style={{
        transitionDelay: `${delay}ms`,
        willChange: "transform, opacity",
      }}
    >
      {children}
    </div>
  );
};

const Home = () => {
  // --- STATE MANAGEMENT ---

  // State cho các thành phần ƯU TIÊN (hiển thị ngay)
  const [movieHero, setMovieHero] = useState([]);
  const [koreanMovies, setKoreanMovies] = useState([]);
  const [chineseMovies, setChineseMovies] = useState([]);
  const [japaneseMovies, setJapaneseMovies] = useState([]);
  const [continueWatching, setContinueWatching] = useState([]);

  // State cho các thành phần TẢI SAU (lazy-load)
  const [newlyAddedMovies, setNewlyAddedMovies] = useState([]);
  const [updatedMovies, setUpdatedMovies] = useState([]);
  const [trendingMovies, setTrendingMovies] = useState([]);
  const [favoriteMovies, setFavoriteMovies] = useState([]);

  // State kiểm soát đăng nhập và trạng thái tải
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    setIsLoggedIn(!!token);
  }, []);

  // --- DATA FETCHING STRATEGY ---

  // BƯỚC 1: Tải dữ liệu ƯU TIÊN để hiển thị giao diện chính nhanh nhất.
  const fetchPriorityData = async () => {
    try {
      const heroPromise = axios.get(`${BASE_URL}/api/phim_hero`);
      const krPromise = axios.get(
        `${BASE_URL}/api/phim_quocgia/?quoc_gia=kr&page=1`
      );
      const cnPromise = axios.get(
        `${BASE_URL}/api/phim_quocgia/?quoc_gia=cn&page=1`
      );
      const jpPromise = axios.get(
        `${BASE_URL}/api/phim_quocgia/?quoc_gia=jp&page=1`
      );

      const [heroRes, krRes, cnRes, jpRes] = await Promise.all([
        heroPromise,
        krPromise,
        cnPromise,
        jpPromise,
      ]);

      setMovieHero(heroRes.data.results);
      setKoreanMovies(krRes.data.results);
      setChineseMovies(cnRes.data.results);
      setJapaneseMovies(jpRes.data.results);
    } catch (error) {
      console.error("Error fetching priority data:", error);
    }
  };

  // BƯỚC 2: Tải dữ liệu "Xem tiếp" (nếu đã đăng nhập). Chạy song song với BƯỚC 1.
  const fetchContinueWatching = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    try {
      const res = await axios.get(`${BASE_URL}/api/video-continue/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContinueWatching(res.data.slice(0, 6));
    } catch (error) {
      console.error("Error fetching continue watching data:", error);
    }
  };

  // BƯỚC 3: Tải dữ liệu THỨ CẤP (phần còn lại của trang).
  const fetchSecondaryData = async () => {
    try {
      const posterPromise = axios.get(`${BASE_URL}/api/phim_poster/`);
      const rankPromise = axios.get(`${BASE_URL}/api/phim_rank/xemnhieunhat/`);
      const updatePromise = axios.get(`${BASE_URL}/api/phim_update/`);
      const favoritePromise = axios.get(
        `${BASE_URL}/api/phim_rank/yeuthichnhat/`
      );

      const [posterRes, rankRes, updateRes, favoriteRes] = await Promise.all([
        posterPromise,
        rankPromise,
        updatePromise,
        favoritePromise,
      ]);

      setNewlyAddedMovies(posterRes.data);
      setTrendingMovies(rankRes.data);
      setUpdatedMovies(updateRes.data);
      setFavoriteMovies(favoriteRes.data);
    } catch (error) {
      console.error("Error fetching secondary data:", error);
    }
  };

  // --- EFFECT HOOKS ---

  useEffect(() => {
    // Khởi tạo timer cho loading cố định 1 giây
    const loadingTimer = setTimeout(() => {
      setInitialLoading(false);
    }, 700);

    // Tải dữ liệu ưu tiên
    fetchPriorityData();
    if (isLoggedIn) {
      fetchContinueWatching();
    }

    // Cleanup timer nếu component unmount
    return () => clearTimeout(loadingTimer);
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchContinueWatching();
    }
  }, [location, isLoggedIn]);

  useEffect(() => {
    if (!initialLoading) {
      const timer = setTimeout(() => {
        fetchSecondaryData();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [initialLoading]);

  return (
    <div className="bg-[#191B24]">
      {initialLoading ? (
        <Loading />
      ) : (
        <>
          <Helmet>
            <title>MePhim - Xem Phim Online Miễn Phí!</title>
            <meta
              name="description"
              content="Trang chủ của MePhim, nơi bạn có thể khám phá và xem hàng ngàn bộ phim chất lượng cao miễn phí."
            />
          </Helmet>
          {/* ---- CÁC THÀNH PHẦN ƯU TIÊN (HIỂN THỊ NGAY) ---- */}
          <AnimatedSection animationId="hero-section" delay={50}>
            <div className="xl:container xl:mx-auto px-4 text-2xl md:space-y-1">
              <HeroSection movies={movieHero} />
            </div>
          </AnimatedSection>

          {isLoggedIn && continueWatching.length > 0 && (
            <AnimatedSection animationId="continue-watching" delay={50}>
              <div className="xl:container xl:mx-auto px-4 pt-4 text-2xl space-x-4 md:space-y-1">
                <ContinueWatching
                  title="Lịch sử xem phim"
                  movies={continueWatching}
                />
              </div>
            </AnimatedSection>
          )}

          <div className="xl:container xl:mx-auto pb-2 px-4 lg:px-0 lg:pb-12 md:space-y-1">
            <div className="bg-none md:bg-[linear-gradient(0deg,_rgba(40,43,58,0)_0%,_rgba(32,35,45,1))] rounded-xl">
              <CategoryCarousel
                title="Phim Hàn Quốc Mới"
                movies={koreanMovies}
                codeCountry="kr"
                nameCountry="Hàn Quốc"
                gradient="linear-gradient(235deg, rgb(255, 255, 255) 30%, rgb(103, 65, 150) 100%)"
              />
              <CategoryCarousel
                title="Phim Trung Quốc Mới"
                movies={chineseMovies}
                codeCountry="cn"
                nameCountry="Trung Quốc"
                gradient="linear-gradient(235deg, rgb(255, 255, 255) 20%, rgb(178, 34, 32) 100%)"
              />
              <CategoryCarousel
                title="Phim Nhật Bản Mới"
                movies={japaneseMovies}
                codeCountry="jp"
                nameCountry="Nhật Bản"
                gradient="linear-gradient(235deg, rgb(255, 255, 255) 30%, rgb(250, 213, 0) 100%)"
              />
            </div>
          </div>

          {/* ---- CÁC THÀNH PHẦN TẢI SAU (LAZY LOADED) ---- */}
          {/* Tất cả các component tải sau giờ đây đều được lazy-load và nằm trong Suspense */}
          <Suspense fallback={<></>}>
            {newlyAddedMovies.length > 0 && (
              <AnimatedSection
                animationId="new-movies"
                animationType="slideInUp"
                delay={50}
              >
                <div className="xl:container xl:mx-auto px-4 py-4 text-2xl space-x-4 md:space-y-1">
                  <PosterCarousel
                    title="Phim mới cập nhật"
                    movies={newlyAddedMovies}
                  />
                </div>
              </AnimatedSection>
            )}

            {updatedMovies.length > 0 && (
              <AnimatedSection
                animationId="updated-movies"
                animationType="scaleIn"
                delay={50}
              >
                <div className="xl:container xl:mx-auto px-4 py-4 text-2xl space-x-4 md:space-y-1">
                  <PosterCarousel
                    title="Phim có tập mới"
                    movies={updatedMovies}
                  />
                </div>
              </AnimatedSection>
            )}

            {trendingMovies.length > 0 && koreanMovies.length > 0 && (
              <AnimatedSection
                animationId="top-phim-bo"
                animationType="slideInUp"
                delay={50}
              >
                <div className=" xl:container xl:mx-auto px-4 py-6 text-2xl space-x-4 md:space-y-1">
                  {/* TopPhimBo giờ đã là component lazy */}
                  <TopPhimBo
                    moviesrank={trendingMovies}
                    koreanMovies={koreanMovies}
                  />
                </div>
              </AnimatedSection>
            )}

            {favoriteMovies.length > 0 && (
              <AnimatedSection
                animationId="rank-movie"
                animationType="slideInUp"
                delay={50}
              >
                <div className="hidden lg:block xl:container xl:mx-auto px-4 py-6 text-2xl space-x-4 md:space-y-1">
                  {/* RankMovie giờ đã là component lazy */}
                  <RankMovie title="Top 10 hôm nay" movies={favoriteMovies} />
                </div>
              </AnimatedSection>
            )}

            <AnimatedSection
              animationId="movie-hot"
              animationType="slideInUp"
              delay={50}
            >
              <div className="xl:container xl:mx-auto px-4 py-4 lg:py-10 text-2xl space-x-4 md:space-y-1">
                <MovieHot title="Phim hành động mãn nhãn" />
              </div>
            </AnimatedSection>

            <AnimatedSection
              animationId="anime-list"
              animationType="slideInUp"
              delay={50}
            >
              <div className="xl:container xl:mx-auto px-4 py-2 lg:py-10 pb-20 text-2xl space-x-4 md:space-y-1">
                <AnimeList title="Kho phim hoạt hình" />
              </div>
            </AnimatedSection>
          </Suspense>
        </>
      )}
    </div>
  );
};

export default Home;
